// modules/BaseWorld/BaseWorldTransition.js
// 基础世界场景转换模块

import { GAME_CONSTANTS } from '../GameConstants.js';
import DebugHelper from '../DebugHelper.js';
import GameStateStore from '../GameStateStore.js';
import AutoSaveManager from '../AutoSaveManager.js';
import { resolveTransitionTarget, findInvalidMapTargets } from '../SceneRegistry.js';
import { transitionToWorldScene } from '../SceneTransitionHelper.js';
import { playSceneSfx } from '../LazyAssetLoader.js';

export default class BaseWorldTransition {
    constructor(scene) {
        this.scene = scene;
    }
    
    /**
     * 设置场景出口（从JSON配置读取）
     */
    setupExits() {
        if (this.scene.isChunkedWorld) {
            DebugHelper.log('分块大地图使用区块传送点，跳过整图出口配置');
            return;
        }

        const exits = this.scene.worldMapJson.exits || [];
        const tileSize = GAME_CONSTANTS.TILE_SIZE;
        const sceneName = this.scene.scene.key || this.scene.mapKey;
        
        if (exits.length === 0) {
            // 检查场景是否使用 towns/caves 作为传送点（如世界地图）
            // 如果使用这些机制，则不需要 exits，不显示警告
            const hasTowns = this.scene.worldMapJson.towns && this.scene.worldMapJson.towns.length > 0;
            const hasCaves = this.scene.worldMapJson.caves && this.scene.worldMapJson.caves.length > 0;
            
            if (!hasTowns && !hasCaves) {
                // 只有在没有使用其他传送机制时才显示警告
                DebugHelper.warn(`${sceneName}: 未找到出口配置`);
            }
            return;
        }

        const invalidTargets = findInvalidMapTargets(this.scene.worldMapJson, sceneName);
        invalidTargets.forEach((issue) => {
            DebugHelper.warn(
                `[SceneRegistry] ${sceneName} 配置了无效出口/传送: ${issue.targetScene} (${issue.source})`
            );
        });
        
        exits.forEach((exit, index) => {
            // 使用网格坐标转换为像素坐标
            const x = exit.tileX * tileSize + (exit.tileWidth * tileSize) / 2;
            const y = exit.tileY * tileSize + (exit.tileHeight * tileSize) / 2;
            const width = exit.tileWidth * tileSize;
            const height = exit.tileHeight * tileSize;
            
            const exitZone = this.scene.add.zone(x, y, width, height);
            this.scene.physics.add.existing(exitZone);
            
            // 只有当玩家精灵存在时才设置碰撞检测
            if (this.scene.playerSprite) {
                this.scene.physics.add.overlap(this.scene.playerSprite, exitZone, () => {
                    this.transitionToScene(exit, { sound: exit.sound || 'into' });
                });
            }
            
            DebugHelper.log(`出口 ${index + 1} 设置完成: (${exit.tileX}, ${exit.tileY}) -> ${exit.targetScene}`);
        });
        
        DebugHelper.log(`共设置了 ${exits.length} 个出口`);
    }
    
    /**
     * 转换到目标场景
     * @param {Object} exit - 出口配置对象
     * @param {Object} options - 选项 { useFade: boolean, sound: string }
     */
    transitionToScene(exit, options = {}) {
        const {
            useFade = true,  // 默认使用淡入淡出
            sound = 'into'   // 默认音效
        } = options;
        
        if (this.scene.isTransitioning) {
            return;
        }

        const resolved = resolveTransitionTarget(exit?.targetScene);
        if (!resolved.valid) {
            DebugHelper.error(`BaseWorldTransition: 无效传送目标 "${exit?.targetScene}"`);
            this._showInvalidTargetMessage(exit?.targetScene);
            return;
        }
        
        this.scene.isTransitioning = true;
        this.scene.movementEnabled = false;
        
        // 停止BGM
        if (this.scene.bgm) {
            this.scene.bgm.stop();
        }
        
        DebugHelper.log(`转换到场景: ${exit.targetScene}`, this.scene.playerSprite ? `${this.scene.playerSprite.x}, ${this.scene.playerSprite.y}` : '玩家精灵不存在');
        
        // 保存游戏状态（同步当前场景的玩家数据）
        if (this.scene.player) {
            GameStateStore.syncFromScene(this.scene.player, {
                includeVehicle: true,
                includePosition: true,
                tilePosition: {
                    tileX: exit.targetTileX,
                    tileY: exit.targetTileY
                },
                inVehicle: this.scene.inVehicle
            });
            GameStateStore.setCurrentScene(resolved.sceneKey);
            GameStateStore.patchPlayer({ currentScene: resolved.sceneKey });
            AutoSaveManager.onSceneTransition();
        }
        
        // 多人游戏模式下，通知服务器场景切换
        if (this.scene.isMultiplayer && this.scene.networkManager) {
            this.scene.networkManager.updatePlayerState({
                sceneId: resolved.sceneKey
            });
            DebugHelper.log('已通知服务器场景切换:', resolved.sceneKey);
        }
        
        const exitForTransition = {
            ...exit,
            targetScene: resolved.sceneKey
        };
        
        // 执行场景转换
        if (useFade) {
            // 使用淡入淡出效果
            this.scene.cameras.main.fadeOut(300, 0, 0, 0);
            
            // 在淡出过程中播放音效
            this.scene.time.delayedCall(50, () => {
                playSceneSfx(this.scene, sound);
            });
            
            // 淡出完成后切换场景
            this.scene.cameras.main.once('camerafadeoutcomplete', () => {
                this._performSceneTransition(exitForTransition);
            });
        } else {
            // 不使用淡入淡出，直接切换
            playSceneSfx(this.scene, sound);
            this._performSceneTransition(exitForTransition);
        }
    }
    
    _showInvalidTargetMessage(targetScene) {
        const label = targetScene ? `"${targetScene}"` : '未知场景';
        const message = `无法前往 ${label}，目标场景未注册。`;

        if (this.scene.worldUI) {
            this.scene.worldUI.showMessage(message, '#ff6666');
        } else {
            DebugHelper.error(`[BaseWorldTransition] ${message}`);
        }
    }
    
    /**
     * 执行场景转换（内部方法）
     * @param {Object} exit - 出口配置对象
     * @private
     */
    _performSceneTransition(exit) {
        transitionToWorldScene(this.scene, exit.targetScene, {
            spawnX: exit.targetTileX,
            spawnY: exit.targetTileY,
            inVehicle: this.scene.inVehicle,
            transitionIn: true
        });
    }
}

